import {
  Button,
  SelectInput,
  AlertBox,
  DatePicker,
  TextInput,
} from "@life-line-lab/components-react";
import * as Yup from "yup";
import React, { useState, useCallback, useEffect } from "react";
import {
  omitBy,
  isNull,
  isEmpty,
  compact,
  uniqBy,
  isNil,
  omit,
  pick,
  over,
} from "lodash";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import { Col, Collapse, Row, TimePicker } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment";
import { ParcelsPickupCreateSchema } from "./pickup-create-validation";
import { render } from "react-dom";
import { format } from "crypto-js";

interface PickupModal {
  info?: any;
  onSubmit?: any;
  onCancel?: any;
  dropOffLocationList?: any;
  getLocationListForModal: (selectedCustomers: string[]) => Promise<any[]>;
  customerList: any[];
}

const { Panel } = Collapse;

const DateTimeHandler = (pickupWindow) => {
  const disabledHours = () => {
    const hours: any = [];

    // openingTime and closingTime
    const startHour = moment(pickupWindow?.start)?.hour();
    const endHour = moment(pickupWindow?.end)?.hour() + 1; // start blocking the next hour

    // disable hours outside openingTime
    for (let i = 0; i < startHour; i++) {
      hours.push(i);
    }

    // disable hours outside closingTime
    for (let i = endHour; i <= 24; i++) {
      hours.push(i);
    }

    return hours;
  };

  const disabledMinutes = (selectedHour) => {
    const minutes: any = [];

    const pickupStart = moment(pickupWindow?.start);
    const pickupEnd = moment(pickupWindow?.end);
    let startMinutes = 0;
    let endMinutes = 59;

    if (selectedHour === pickupStart.hour()) {
      // if openingTime is 10.30, we let them start from 30 (if 10 is selected)
      startMinutes = pickupStart.minutes();
    } else if (selectedHour === pickupEnd.hour()) {
      // if closingTime is 10.30, we let them end at 30 (if 10 is selected)
      endMinutes = pickupEnd.minutes() + 1;
    }

    for (let i = 0; i < startMinutes; i++) {
      minutes.push(i);
    }

    for (let i = endMinutes; i <= 59; i++) {
      minutes.push(i);
    }

    return minutes;
  };

  return {
    disabledHours,
    disabledMinutes,
  };
};

const PickupFormHandler = (props) => {
  const { initialError, setError, setInput, input, dayName, info } =
    props || {};

  const handlePickUpWindow = (value?) => {
    let tempOpTime = { start: null, end: null };

    // if no value is passed, use dayName
    //   handles state dependent on another state
    const compDay = value ? value.format("dddd").toUpperCase() : dayName;

    for (let i = 0; i < info?.selectedOrders?.length; i++) {
      let customer = info?.selectedOrders[i]?.senderLocationData;
      let currentOpTime;

      // if one customer has no working hours, disallow filling of fields
      if (!customer?.workingHours || isEmpty(customer?.workingHours)) {
        break;
      }

      // otherwise, get the customer openingTime and closingTime
      for (let j = 0; j < customer?.workingHours?.length; j++) {
        const hour = customer?.workingHours[j];
        if (hour?.day === compDay) {
          currentOpTime = { start: hour?.openingTime, end: hour?.closingTime };
          break;
        }
      }

      // if compDay doesnt match any hour?.day, no working hour exists for that day, so do not let user fill
      if (!isEmpty(currentOpTime)) {
        // multiple customers may have different operation times
        //   if tempOpTime is null, fill it in
        if (tempOpTime.start === null) {
          tempOpTime = { ...currentOpTime };
        } else {
          // if currentOpTime.start > tempOpTime.start, fill in that time
          //   dont use .isBefore or .isAfter, because Moment contains dates
          if (moment(currentOpTime.start).isAfter(moment(tempOpTime.start))) {
            tempOpTime = { ...tempOpTime, start: currentOpTime.start };
          }

          // if currentOpTime.end < tempOpTime.end, fill in that time
          if (moment(currentOpTime.end).isAfter(moment(tempOpTime.end))) {
            tempOpTime = { ...tempOpTime, end: currentOpTime.end };
          }
        }
      } else {
        // clear tempOpTime to prevent users to fill
        tempOpTime = { start: null, end: null };
        break;
      }
    }

    // setting up of available time
    return tempOpTime;
  };

  const handleSelectChange = (value: string | string[] | {}, name: string) => {
    setError(initialError);
    switch (name) {
      case "customerList":
        setInput({ ...input, [name]: value, customerLocation: [] });
        break;
      default:
        setInput({ ...input, [name]: value });
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleTimeChange = (value: any) => {
    setError({ initialError });

    // value contains an array of 2 elements: [Moment(startTime), Moment(endTime)]
    setInput({ ...input, startTime: value[0], endTime: value[1] });
  };

  return {
    handlePickUpWindow,
    handleSelectChange,
    handleChange,
    handleTimeChange,
  };
};

const CustomerContainer = ({ info, dayName }) => {
  const { t } = useTranslation();
  const uniqueOrders: any = uniqBy(info?.selectedOrders, "individualName");

  const handleWorkingHour = useCallback(() => {
    // gets and returns all the working hours corresponding to dayName
    const getWorkingHour = (order) => {
      const operationalHours = order?.senderLocationData?.workingHours?.map(
        (hour, index) =>
          hour?.day === dayName && (
            <div key={index}>{`${moment(hour?.openingTime).format(
              "hh:mm a",
            )} - ${moment(hour?.closingTime).format("hh:mm a")}`}</div>
          ),
      );

      if (isEmpty(compact(operationalHours))) {
        return t("txt.noOperationalHours");
      } else {
        return operationalHours;
      }
    };

    return (
      <>
        {info &&
          uniqueOrders?.map((order, index) => (
            <Row key={index} gutter={[8, 0]}>
              <Col span={2}>{index + 1}.</Col>
              <Col span={10}>
                {order?.individualName
                  ? order?.individualName.length > 25
                    ? `${order?.individualName?.substring(0, 25)}...`
                    : order?.individualName
                  : t("txt.none")}
              </Col>
              <Col span={12}>{getWorkingHour(order)}</Col>
            </Row>
          ))}
      </>
    );
  }, [dayName]);

  return (
    <Collapse defaultActiveKey={["1"]}>
      <Panel header={t("txt.customers")} key={1} className="pu-panel">
        <div className="pu-panel-inner">
          <Row gutter={[8, 0]}>
            <Col span="none">{t("label.pickupDay")}:</Col>
            <Col span="auto">{dayName}</Col>
          </Row>
          <Row gutter={[8, 0]}>
            <Col span={2} className="pickup-heading">
              {t("label.no")}.
            </Col>
            <Col span={10} className="pickup-heading">
              {t("label.customerName")}
            </Col>
            <Col span={12} className="pickup-heading">
              {t("label.operationalHours")}
            </Col>
            <Col span={24}>{handleWorkingHour()}</Col>
          </Row>
        </div>
      </Panel>
    </Collapse>
  );
};

const PickupModal: React.FC<PickupModal> = ({
  info,
  onSubmit,
  onCancel,
  dropOffLocationList,
  customerList,
  getLocationListForModal,
}) => {
  const initialInput = {
    pickupDate: info?.selectedDeliveryDate
      ? moment(info?.selectedDeliveryDate)
      : null,
    startTime: null,
    endTime: null,
    dropOffLocation: "",
    pickupInstructions: "",
    customerList: [] as string[],
    customerLocation: [] as string[],
  };

  const { t } = useTranslation();
  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [isOperational, setIsOperational] = useState<boolean>(true);
  const [dayName, setDayName] = useState<any>(
    info?.selectedDeliveryDate
      ? moment(info?.selectedDeliveryDate).format("dddd").toUpperCase()
      : moment().format("dddd").toUpperCase(),
  );
  const [customerLocation, setCustomerLocation] = useState<any[]>([]);

  const pickupFormObjects = {
    initialError,
    setError,
    setInput,
    input,
    info,
    dayName,
  };

  const {
    handleSelectChange,
    handleChange,
    handleTimeChange,
    handlePickUpWindow,
  } = PickupFormHandler(pickupFormObjects);

  const [pickupWindow, setPickupWindow] = useState<any>(handlePickUpWindow());
  // const { disabledHours, disabledMinutes } = DateTimeHandler(pickupWindow);

  const handleDateChange = (value: moment.Moment, name: string) => {
    setError(initialError);

    const prevDate = input[name];
    const isSameDay =
      prevDate && value && moment(prevDate).isSame(value, "day");

    if (!isSameDay) {
      setInput({ ...input, [name]: value, startTime: null, endTime: null });
    } else {
      setInput({ ...input, [name]: value });
    }

    setDayName && setDayName(moment(value).format("dddd").toUpperCase());

    const pickUpWindowValue = handlePickUpWindow(value);
    if (pickUpWindowValue?.start === null)
      setIsOperational && setIsOperational(false);
    else {
      setIsOperational && setIsOperational(true);
      setPickupWindow && setPickupWindow(handlePickUpWindow(value));
    }
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        pickupDate: input.pickupDate
          ? moment(input.pickupDate).toISOString()
          : null,
        pickupTimeFrom: input.startTime
          ? moment(input.startTime).toISOString()
          : null,
        pickupTimeTo: input.endTime
          ? moment(input.endTime).toISOString()
          : null,
        dropOffLocationID: input.dropOffLocation ? input.dropOffLocation : null,
        pickupInstructions: input.pickupInstructions
          ? input.pickupInstructions
          : null,
        customerList: input.customerList,
        customerLocation: input.customerLocation,
        isEmptyPickup: info?.selectedOrders?.length === 0,
      };

      if (isSameTime) {
        setError({
          ...error,
        });
        return;
      }

      const value = omit(
        ParcelsPickupCreateSchema.validateSync(omitBy(valInput, isNull), {
          strict: false,
          abortEarly: true,
          stripUnknown: true,
        }),
        ["isEmptyPickup", "customerList", "customerLocation"],
      );

      if (info?.selectedOrders?.length > 0) {
        onSubmit("pickup", {
          ...value,
          orderIDs: info?.selectedOrders?.map((order: any) => order._id),
        });
      } else {
        const customerMap = new Map(customerList.map((c) => [c._id, c]));
        const locationValueMap = new Map();

        customerLocation.forEach((loc) => {
          loc.options.forEach((opt) => {
            locationValueMap.set(opt.value, { label: loc.label, option: opt });
          });
        });

        const structuredCustomers = input.customerList
          .map((id) => {
            const customer = customerMap.get(id);
            return customer ? { id: customer._id, name: customer.name } : null;
          })
          .filter(Boolean);

        const structuredLocations = input.customerLocation
          .map((locationInput) => {
            const locationData = locationValueMap.get(locationInput);
            if (!locationData) return null;

            const customer = structuredCustomers.find(
              (c) => c?.name === locationData.label,
            );
            return {
              locationID: locationInput,
              merchantID: customer?.id || "unknown",
            };
          })
          .filter(Boolean);

        const model = (data: any) => {
          return omitBy(
            {
              locationID: data.locationID,
              noOfParcels: "0",
              merchantID: data.merchantID,
              dropOffLocationID: value.dropOffLocationID,
              status: "PENDING_PICKUP",
            },
            isNil,
          );
        };

        const normalizePickupTask = structuredLocations.map((location) =>
          model(location),
        );

        const merchantIDs = new Set(
          normalizePickupTask.map((task) => task.merchantID),
        );

        const invalidCustomers = structuredCustomers.filter(
          (customer) => !merchantIDs.has(customer?.id),
        );

        if (invalidCustomers.length > 0)
          throw new Yup.ValidationError(
            `Location for customer not selected`,
            null,
            "customerLocation",
            "min",
          );

        onSubmit("pickup", {
          ...value,
          pickupTasks: normalizePickupTask,
        });
      }

      onCancel();
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      if (input.customerList.length === 0) {
        setCustomerLocation([]);
        setAllLocations([]);
        setInput((prev) => ({
          ...prev,
          pickupDate: null,
          startTime: null,
          endTime: null,
        }));
        return;
      }

      try {
        const allCustomerLocations = await getLocationListForModal(
          input.customerList,
        );
        setAllLocations(allCustomerLocations);
        const locationsByOrg = {};

        allCustomerLocations.forEach((location) => {
          if (!locationsByOrg[location.orgName]) {
            locationsByOrg[location.orgName] = [];
          }

          locationsByOrg[location.orgName].push({
            label: location.primary ? "Primary Address" : location.locationName,
            value: location._id,
          });
        });

        const locationOptions = Object.keys(locationsByOrg).map((orgName) => ({
          label: orgName,
          options: locationsByOrg[orgName],
        }));

        setCustomerLocation(locationOptions);

        setInput((prev) => ({
          ...prev,
          pickupDate: null,
          startTime: null,
          endTime: null,
        }));
      } catch (error) {
        console.error("Error fetching customer locations:", error);
        setCustomerLocation([]);
        setAllLocations([]);
        setInput((prev) => ({
          ...prev,
          pickupDate: null,
          startTime: null,
          endTime: null,
        }));
      }
    };

    fetchLocations();
  }, [input.customerList]);

  const [selectedCustomers, setSelectedCustomers] = useState<any[]>([]);

  useEffect(() => {
    const selected = customerList.filter((customer) =>
      input.customerList.includes(customer._id),
    );
    setSelectedCustomers(selected);
  }, [input.customerList, customerList]);

  const [allLocations, setAllLocations] = useState<any[]>([]);

  const getSelectedLocationsWithWorkingHours = () => {
    const result: any[] = [];
    input.customerLocation.forEach((locationId: string) => {
      const location = allLocations.find((loc) => loc._id === locationId);
      if (!location) {
        return;
      }
      const customer = customerList.find((c) => c._id === location.orgID);
      if (!customer) {
        return;
      }
      result.push({ customer, location });
    });
    return result;
  };

  const getCustomersTimeWindow = (
    selectedLocations: any[],
    day: string,
  ): { start: string; end: string } | null => {
    let tempOpTime: { start: string | null; end: string | null } = {
      start: null,
      end: null,
    };

    for (const { location } of selectedLocations) {
      if (
        !Array.isArray(location.workingHours) ||
        location.workingHours.length === 0
      ) {
        return null;
      }
      const workingHour = (location.workingHours || []).find(
        (hour: any) => hour.day?.toUpperCase() === day?.toUpperCase(),
      );
      if (!workingHour) {
        return null;
      }
      if (tempOpTime.start === null) {
        tempOpTime = {
          start: workingHour.openingTime,
          end: workingHour.closingTime,
        };
      } else {
        const currentStart = moment(tempOpTime.start).format("HH:mm");
        const currentEnd = moment(tempOpTime.end).format("HH:mm");
        const newStart = moment(workingHour.openingTime).format("HH:mm");
        const newEnd = moment(workingHour.closingTime).format("HH:mm");

        if (newStart > currentStart) {
          tempOpTime.start = workingHour.openingTime;
        }
        if (newEnd < currentEnd) {
          tempOpTime.end = workingHour.closingTime;
        }
      }
    }
    if (tempOpTime.start && tempOpTime.end) {
      return { start: tempOpTime.start, end: tempOpTime.end };
    }
    return null;
  };

  let disabledHours: any, disabledMinutes: any;

  const getDisabledTimes = () => {
    const selectedLocations = getSelectedLocationsWithWorkingHours();
    if (!input.pickupDate || !selectedLocations.length) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }

    const day = moment(input.pickupDate).format("dddd").toUpperCase();

    const overlappingHours = getCustomersTimeWindow(selectedLocations, day);

    if (!overlappingHours) {
      return {
        // If no overlapping hours, disable all hours and minutes
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    const startHour = moment(overlappingHours.start).hour();
    const endHour = moment(overlappingHours.end).hour();
    const startMinutes = moment(overlappingHours.start).minute();
    const endMinutes = moment(overlappingHours.end).minute();

    return {
      disabledHours: () => {
        const hours: number[] = [];
        for (let i = 0; i < 24; i++) {
          if (i < startHour || i > endHour) {
            hours.push(i);
          }
        }
        return hours;
      },

      disabledMinutes: (selectedHour: number) => {
        if (selectedHour === startHour && selectedHour === endHour) {
          return [
            ...Array.from({ length: startMinutes }, (_, i) => i),
            ...Array.from(
              { length: 60 - endMinutes - 1 },
              (_, i) => i + endMinutes + 1,
            ),
          ];
        }
        if (selectedHour === startHour) {
          return Array.from({ length: startMinutes }, (_, i) => i);
        }
        if (selectedHour === endHour) {
          return Array.from(
            { length: 60 - endMinutes - 1 },
            (_, i) => endMinutes + i + 1,
          );
        }
        return [];
      },
    };
  };

  if (info.selectedOrders?.length > 0) {
    ({ disabledHours, disabledMinutes } = DateTimeHandler(pickupWindow));
  } else {
    ({ disabledHours, disabledMinutes } = getDisabledTimes());
  }

  const isDateOperationalForAll = (date: moment.Moment) => {
    const selectedLocations = getSelectedLocationsWithWorkingHours();
    if (!selectedLocations.length) return false;
    const day = date.format("dddd").toUpperCase();

    // Check if all selected customers have working hours for the given day
    return selectedLocations.every(
      ({ location }) =>
        Array.isArray(location.workingHours) &&
        location.workingHours.some(
          (hour: any) => hour.day?.toUpperCase() === day,
        ),
    );
  };

  const renderWorkingHours = (customer: any, location: any) => {
    if (location.workingHours.length === 0) {
      return <span>{t("txt.noOperationalHours")}</span>;
    }

    const workingHour = location.workingHours.find(
      (hour: any) => hour.day?.toUpperCase() === dayName?.toUpperCase(),
    );

    if (!workingHour) {
      return <div>{t("txt.noOperationalHours")}</div>;
    }

    return (
      <div>
        <b>{workingHour.day}:</b>{" "}
        {moment(workingHour.openingTime).format("hh:mm a")} -{" "}
        {moment(workingHour.closingTime).format("hh:mm a")}
      </div>
    );
  };

  const isSameTime =
    !!input.startTime &&
    !!input.endTime &&
    moment(input.startTime).format("HH:mm") ===
      moment(input.endTime).format("HH:mm");

  return (
    <div className="pickup-modal">
      <div className="modal-content-title">
        {t("action.createPickUp").toLocaleUpperCase()}
      </div>
      {info?.selectedOrders?.length > 0 && (
        <div className="pickup-content pickup-customer-summary">
          <CustomerContainer info={info} dayName={dayName} />
        </div>
      )}
      <div className="pickup-content">
        <Row gutter={[16, 16]}>
          {info.selectedOrders?.length === 0 && (
            <>
              <Col span={12}>
                <SelectInput
                  showSearch
                  options={customerList?.map((customer) => {
                    return {
                      label: customer.name,
                      value: customer._id,
                    };
                  })}
                  isRequired
                  value={input.customerList}
                  name="customerList"
                  placeholder={t("placeholder.filterByCustomer")}
                  label={t("label.customer")}
                  onChange={(value) =>
                    handleSelectChange(value, "customerList")
                  }
                  mode="multiple"
                  showArrow={true}
                />
                <AlertBox text={error.customerList} />
              </Col>
              {info.selectedOrders?.length === 0 &&
                input.customerList.length > 0 &&
                input.customerLocation.length > 0 && (
                  <Col
                    span={24}
                    style={{
                      marginBottom: 16,
                      background: "#f7f7f7",
                      border: "1px solid #d9d9d9",
                      borderRadius: 10,
                      padding: "16px",
                    }}
                  >
                    <div className="pu-panel-inner">
                      <Col span={12}>{t("label.operationalHours")}</Col>
                      {getSelectedLocationsWithWorkingHours().map(
                        ({ customer, location }, idx) => (
                          <div key={location._id} style={{ marginBottom: 8 }}>
                            <div className="pickup-heading">
                              <b>{customer.name}</b> -{" "}
                              <b>
                                {location.locationName ||
                                  location.address?.street1}
                              </b>
                            </div>
                            {renderWorkingHours(customer, location)}
                            {idx <
                              getSelectedLocationsWithWorkingHours().length -
                                1 && (
                              <div
                                style={{
                                  borderTop: "1px solid #eee",
                                  margin: "8px 0",
                                }}
                              ></div>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  </Col>
                )}
              <Col span={12}>
                <SelectInput
                  showSearch
                  options={customerLocation}
                  isRequired
                  name="customerLocation"
                  placeholder={t("placeholder.filterByCustomerLocation")}
                  label={t("label.customerLocation")}
                  onChange={(value) =>
                    handleSelectChange(value, "customerLocation")
                  }
                  value={input.customerLocation}
                  mode="multiple"
                  showArrow={true}
                  allowClear
                />
                <AlertBox text={error.customerLocation} />
              </Col>
            </>
          )}
          <Col span={24}>
            <DatePicker
              isRequired={true}
              isError={!!error.pickupDate}
              suffix={<CalendarOutlined />}
              label={t("label.pickupDate")}
              placeholder="YYYY-MM-DD"
              defaultValue={info?.selectedOrders[0]?.deliveryDate}
              value={input.pickupDate ? moment(input.pickupDate) : null}
              onChange={(value: any) => handleDateChange(value, "pickupDate")}
              disabledDate={(current) => {
                if (!current) return false;
                // Disable dates before today
                const today = moment().startOf("day");
                if (current && current.isBefore(today)) {
                  return true;
                }

                if (info.selectedOrders?.length > 0) {
                  // Use original logic for selectedOrders mode
                  const window = handlePickUpWindow(current);
                  return !window?.start || !window?.end;
                }

                // Multi-customer mode: only enable dates where all customers are operational
                return !isDateOperationalForAll(current);
              }}
            />
            <AlertBox text={error.pickupDate} />
          </Col>
          <Col span={24}>
            <TimePicker.RangePicker
              disabled={!isOperational && info.selectedOrders?.length > 0}
              value={[input.startTime, input.endTime]}
              onChange={(value) => {
                handleTimeChange(value);
              }}
              disabledHours={disabledHours}
              disabledMinutes={disabledMinutes}
              format={"hh:mm a"}
            />
            <AlertBox text={error.pickupTimeFrom || error.pickupTimeTo} />
            {isSameTime && (
              <AlertBox text={t("error.compareValue")} type="error" />
            )}
          </Col>
          <Col span={24}>
            <SelectInput
              options={dropOffLocationList?.map((location) => {
                return {
                  label: location.locationName,
                  value: location._id,
                };
              })}
              isRequired
              name="dropOffLocation"
              placeholder={t("placeholder.unloadLocation")}
              label={t("label.unloadLocation")}
              onChange={(value) => handleSelectChange(value, "dropOffLocation")}
            />
            <AlertBox text={error.dropOffLocationID} />
          </Col>
          <Col span={24}>
            <TextInput
              isRequired={false}
              isError={!!error.pickupInstructions}
              label={t("label.pickupInstructions")}
              name="pickupInstructions"
              value={input.pickupInstructions}
              onChange={(value) => handleChange(value)}
            />
            <AlertBox text={error.pickupInstructions} />
          </Col>
        </Row>
      </div>
      <div className="right modal-end-button">
        <Button
          isDisabled={
            (!isOperational && info.selectedOrders?.length > 0) || isSameTime
          }
          type={"primary"}
          label={t("action.confirm")}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={() => {
            onCancel();
          }}
        />
      </div>
    </div>
  );
};

export default PickupModal;
